import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IonIcon } from "@ionic/react";
import { playBack, playForward } from "ionicons/icons";
import { EventName, HalfEvent, MatchPhone } from "refsix-js-models";
import { ReplayMatchEvent, ReplayTick, SeekCommand, VideoDetails } from "../replayTypes";
import {
    matchTime as formatMatchTime,
    overlayTimeDuringBreak,
    playingSegmentLengthSecs,
} from "../replayUtils";
import ReplayScore from "./replayScore";
import VideoRequestFile from "./videoRequestFile";

interface VideoPlayerProps {
    match: MatchPhone;
    matchTimings: HalfEvent[];
    matchEvents: ReplayMatchEvent[];
    videoDetails: VideoDetails;
    seekCommand: SeekCommand | null;
    currentTimestamp: number;
    onTick: (tick: ReplayTick) => void;
    onPlayStateChange: (playing: boolean) => void;
    onRequestFile: () => void;
}

/**
 * Plays the calibrated match video and, on every match-second, reports the
 * current playback position so the GPS/HR/score overlays can stay in sync.
 * Ported from the legacy VideoPlayerDirective.
 */
const VideoPlayer: FC<VideoPlayerProps> = ({
    match,
    matchTimings,
    matchEvents,
    videoDetails,
    seekCommand,
    currentTimestamp,
    onTick,
    onPlayStateChange,
    onRequestFile,
}) => {
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [overlayText, setOverlayText] = useState<string>("00:00");

    // Mutable playback state kept in refs so the rAF loop never reads stale values.
    const playingRef = useRef(false);
    const currentMatchSecondRef = useRef(0);
    const currentMatchSegmentRef = useRef(0);
    const duringHalfTimeRef = useRef(false);
    const startOffsetRef = useRef(0);
    const rafRef = useRef<number | null>(null);

    // Keep latest callbacks in refs to avoid re-attaching listeners each render.
    const onTickRef = useRef(onTick);
    const onPlayStateChangeRef = useRef(onPlayStateChange);
    useEffect(() => {
        onTickRef.current = onTick;
        onPlayStateChangeRef.current = onPlayStateChange;
    }, [onTick, onPlayStateChange]);

    const hasVideo = !!videoDetails.url;

    useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return;
        }
        const offsets = videoDetails.playSegmentOffsets;

        const calcMatchTime = (): number => {
            duringHalfTimeRef.current = true;
            for (let i = 0; i < offsets.length; i++) {
                // Guard against sparse/holey calibration data.
                if (!offsets[i]) {
                    continue;
                }
                if (
                    video.currentTime >= offsets[i].offsetSec &&
                    video.currentTime <=
                        offsets[i].offsetSec + offsets[i].durationSec
                ) {
                    startOffsetRef.current = offsets[i].offsetSec;
                    currentMatchSegmentRef.current = i;
                    duringHalfTimeRef.current = false;
                    break;
                }
            }
            const adjustedTime = video.currentTime - startOffsetRef.current;
            return adjustedTime < 0 ? 0 : adjustedTime;
        };

        const updateOverlay = (matchTimeSecs: number) => {
            if (duringHalfTimeRef.current) {
                setOverlayText(
                    overlayTimeDuringBreak(offsets, video.currentTime, t)
                );
            } else {
                const segment = currentMatchSegmentRef.current;
                const lengthSecs = playingSegmentLengthSecs(
                    matchTimings[segment]
                );
                setOverlayText(
                    formatMatchTime(
                        {
                            offset: matchTimeSecs * 1000,
                            minOfHalf: offsets[segment]?.minuteOfPlayMilli || 0,
                        },
                        lengthSecs
                    )
                );
            }
        };

        const matchTimestamp = (matchTimeSecs: number, segment: number) =>
            (matchTimings[segment]?.timestamp || 0) + matchTimeSecs * 1000;

        const playLoop = () => {
            const videoSecond = Math.floor(video.currentTime);
            const matchTimeSecs = Math.floor(calcMatchTime());
            if (currentMatchSecondRef.current !== matchTimeSecs) {
                updateOverlay(matchTimeSecs);
                currentMatchSecondRef.current = matchTimeSecs;
                onTickRef.current({
                    second: matchTimeSecs,
                    segment: currentMatchSegmentRef.current,
                    videoSecond,
                    timestamp: matchTimestamp(
                        matchTimeSecs,
                        currentMatchSegmentRef.current
                    ),
                });
            }
            if (playingRef.current) {
                render();
            }
        };

        const render = () => {
            rafRef.current = requestAnimationFrame(playLoop);
        };

        const onPlay = () => {
            playingRef.current = true;
            onPlayStateChangeRef.current(true);
            render();
        };
        const onPause = () => {
            playingRef.current = false;
            onPlayStateChangeRef.current(false);
        };
        const onSeek = () => {
            if (!playingRef.current) {
                render();
            }
        };

        video.addEventListener("play", onPlay);
        video.addEventListener("pause", onPause);
        video.addEventListener("ended", onPause);
        video.addEventListener("seeked", onSeek);
        video.addEventListener("seeking", onSeek);

        // Start playback from the calibrated kick-off of the first half (not the
        // very start of the video, which may include pre-match footage).
        const onLoadedMetadata = () => {
            try {
                video.currentTime = offsets[0].offsetSec;
            } catch (err) {
                console.warn("Replay: unable to seek to first-half start", err);
            }
            video.play().catch((err) =>
                console.warn("Replay: video play failed", err)
            );
        };

        if (videoDetails.isCalibrated && videoDetails.url && offsets.length) {
            startOffsetRef.current = offsets[0].offsetSec;
            video.addEventListener("loadedmetadata", onLoadedMetadata, {
                once: true,
            });
            video.src = videoDetails.url;
            // If metadata is already available (e.g. cached), seek immediately.
            if (video.readyState >= 1) {
                onLoadedMetadata();
            }
        }

        return () => {
            video.removeEventListener("play", onPlay);
            video.removeEventListener("pause", onPause);
            video.removeEventListener("ended", onPause);
            video.removeEventListener("seeked", onSeek);
            video.removeEventListener("seeking", onSeek);
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, [videoDetails.url, videoDetails.isCalibrated, matchTimings, t]);

    // Respond to seek commands from the incident list.
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !seekCommand || !videoDetails.playSegmentOffsets.length) {
            return;
        }
        const { position, segmentIndex, type } = seekCommand;
        let seekMs = position;
        if (type !== EventName.half) {
            seekMs = seekMs - 30000 < 0 ? 0 : seekMs - 30000;
        }
        const offset =
            videoDetails.playSegmentOffsets[segmentIndex]?.offsetSec || 0;
        video.currentTime = offset + seekMs / 1000;
    }, [seekCommand, videoDetails.playSegmentOffsets]);

    const seekRelative = (relativeSeconds: number) => {
        const video = videoRef.current;
        if (!video) {
            return;
        }
        let newTime = video.currentTime + relativeSeconds;
        if (newTime < 0) {
            newTime = 0;
        }
        if (newTime > video.duration) {
            newTime = video.duration;
        }
        video.currentTime = newTime;
    };

    return (
        <div className={"video-container" + (hasVideo ? "" : " empty")}>
            <video ref={videoRef} controls />
            {!hasVideo && <VideoRequestFile onRequestFile={onRequestFile} />}
            <div className="video-overlay">
                <div className="top-left">
                    <div className="team-name home">
                        {match.homeTeamShort}
                        <i
                            className="team-color home"
                            style={{ backgroundColor: match.homeColor }}
                        />
                    </div>
                    <ReplayScore
                        matchEvents={matchEvents}
                        currentTimestamp={currentTimestamp}
                    />
                    <div className="team-name away">
                        {match.awayTeamShort}
                        <i
                            className="team-color away"
                            style={{ backgroundColor: match.awayColor }}
                        />
                    </div>
                    <div className="time video-current-time">{overlayText}</div>
                </div>

                <div className="left-side">
                    <button
                        type="button"
                        className="play-control rewind"
                        onClick={() => seekRelative(-10)}
                    >
                        <IonIcon icon={playBack} />
                    </button>
                </div>
                <div className="right-side">
                    <button
                        type="button"
                        className="play-control fastforward"
                        onClick={() => seekRelative(10)}
                    >
                        <IonIcon icon={playForward} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VideoPlayer;
